import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  documentId,
} from "firebase/firestore";

const QuizResults = () => {
  const { quizCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState({});
  const [questions, setQuestions] = useState({});
  const [isHoveringBack, setIsHoveringBack] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  
  // Toggle question expansion to show/hide mark scheme
  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Fetch all data in parallel
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch the quiz data
        const quizzesRef = collection(db, "quizzes");
        const quizQuery = query(quizzesRef, where("quizCode", "==", quizCode));
        const quizSnapshot = await getDocs(quizQuery);
        
        if (quizSnapshot.empty) {
          setError(`No quiz found with code: ${quizCode}`);
          setLoading(false);
          return;
        }
        
        const quizDoc = quizSnapshot.docs[0];
        const quiz = { id: quizDoc.id, ...quizDoc.data() };
        setQuizData(quiz);
        
        // 2. Fetch all submissions for this quiz
        const submissionsRef = collection(db, "submissions");
        const submissionsQuery = query(submissionsRef, where("quizCode", "==", quizCode));
        const submissionsSnapshot = await getDocs(submissionsQuery);
        
        if (submissionsSnapshot.empty) {
          // No submissions yet, but we still want to show the quiz questions
          if (quiz.questionIds && quiz.questionIds.length > 0) {
            const questionsData = await fetchQuestions(quiz.questionIds);
            setQuestions(questionsData);
          }
          setLoading(false);
          return;
        }
        
        // Process all submissions
        const allSubmissions = submissionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setSubmissions(allSubmissions);
        
        // 3. Extract unique user IDs and question IDs from submissions
        const userIdSet = new Set();
        const questionIdSet = new Set();
        
        allSubmissions.forEach(submission => {
          if (submission.userId) {
            userIdSet.add(submission.userId);
          }
          
          if (submission.questionId) {
            // Handle paths like "questions/someID"
            const questionId = submission.questionId.includes("/") 
              ? submission.questionId.split("/").pop() 
              : submission.questionId;
            questionIdSet.add(questionId);
          }
        });
        
        // Add quiz question IDs to ensure we fetch all questions
        if (quiz.questionIds) {
          quiz.questionIds.forEach(id => questionIdSet.add(id));
        }
        
        // 4. Fetch all users and questions in parallel
        const [usersData, questionsData] = await Promise.all([
          fetchUsers(Array.from(userIdSet)),
          fetchQuestions(Array.from(questionIdSet))
        ]);
        
        setUsers(usersData);
        setQuestions(questionsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching quiz results:", error);
        setError("Failed to load quiz results. Please try again.");
        setLoading(false);
      }
    };
    
    // Fetch multiple users in a single batch
    const fetchUsers = async (userIds) => {
      if (!userIds || userIds.length === 0) return {};
      
      try {
        // Split into batches of 10 (Firestore limit for 'in' queries)
        const userMap = {};
        
        // Process in batches of 10
        for (let i = 0; i < userIds.length; i += 10) {
          const batch = userIds.slice(i, i + 10);
          const usersRef = collection(db, "users");
          const usersQuery = query(usersRef, where(documentId(), "in", batch));
          const usersSnapshot = await getDocs(usersQuery);
          
          usersSnapshot.docs.forEach(doc => {
            userMap[doc.id] = doc.data();
          });
        }
        
        return userMap;
      } catch (error) {
        console.error("Error fetching users:", error);
        return {};
      }
    };
    
    // Fetch multiple questions in a single batch
    const fetchQuestions = async (questionIds) => {
      if (!questionIds || questionIds.length === 0) return {};
      
      try {
        // Split into batches of 10 (Firestore limit for 'in' queries)
        const questionMap = {};
        
        // Process in batches of 10
        for (let i = 0; i < questionIds.length; i += 10) {
          const batch = questionIds.slice(i, i + 10);
          const questionsRef = collection(db, "questions");
          const questionsQuery = query(questionsRef, where(documentId(), "in", batch));
          const questionsSnapshot = await getDocs(questionsQuery);
          
          questionsSnapshot.docs.forEach(doc => {
            questionMap[doc.id] = {
              id: doc.id,
              ...doc.data()
            };
          });
        }
        
        return questionMap;
      } catch (error) {
        console.error("Error fetching questions:", error);
        return {};
      }
    };
    
    fetchAllData();
  }, [quizCode]);
  
  // Compute student data from submissions, users, and questions
  const students = useMemo(() => {
    if (!submissions.length || !Object.keys(users).length || !Object.keys(questions).length) {
      return [];
    }
    
    // Group submissions by user
    const submissionsByUser = {};
    
    submissions.forEach(submission => {
      const userId = submission.userId;
      if (!userId) return;
      
      if (!submissionsByUser[userId]) {
        submissionsByUser[userId] = [];
      }
      
      submissionsByUser[userId].push(submission);
    });
    
    // Calculate scores for each user
    return Object.keys(submissionsByUser).map(userId => {
      const userData = users[userId] || {};
      const userSubmissions = submissionsByUser[userId];
      
      // Calculate total score
      let totalScore = 0;
      let totalMaxScore = 0;
      
      userSubmissions.forEach(submission => {
        const markAwarded = parseFloat(submission.markAwarded) || 0;
        totalScore += markAwarded;
        
        // Get question max mark
        if (submission.questionId) {
          const questionId = submission.questionId.includes("/") 
            ? submission.questionId.split("/").pop() 
            : submission.questionId;
          
          const questionData = questions[questionId];
          if (questionData) {
            totalMaxScore += parseFloat(questionData.maxMark) || 0;
          }
        }
      });
      
      return {
        id: userId,
        username: userData.username || userData.displayName || "Unknown",
        email: userData.email || "No email",
        score: totalScore,
        maxScore: totalMaxScore,
        percentage: totalMaxScore > 0 ? ((totalScore / totalMaxScore) * 100).toFixed(2) : 0,
        submissions: userSubmissions,
      };
    });
  }, [submissions, users, questions]);
  
  // Compute question statistics from submissions and questions
  const questionStats = useMemo(() => {
    if (!submissions.length || !Object.keys(questions).length || !quizData?.questionIds) {
      return [];
    }
    
    // Create a map to store statistics for each question
    const questionStatsMap = {};
    
    // Initialize stats for each question in the quiz
    quizData.questionIds.forEach(qId => {
      const question = questions[qId] || {};
      
      questionStatsMap[qId] = {
        id: qId,
        totalAttempts: 0,
        totalScore: 0,
        totalPossibleScore: 0,
        averagePercentage: 0,
        notAnsweredCount: 0,
        questionText: question.questionText || "Image-based question",
        maxMark: parseFloat(question.maxMark) || 0,
        questionImageURL: question.questionImageURL || ""
      };
    });
    
    // Process all submissions
    submissions.forEach(submission => {
      if (!submission.questionId) return;
      
      const questionId = submission.questionId.includes("/") 
        ? submission.questionId.split("/").pop() 
        : submission.questionId;
      
      // Skip if this question is not part of the quiz
      if (!questionStatsMap[questionId]) return;
      
      // Update statistics
      questionStatsMap[questionId].totalAttempts++;
      
      // Check if the answer was "Not answered by student"
      if (submission.userAnswer === "Question not answered by student" || 
          submission.userAnswer === "Not answered by student") {
        questionStatsMap[questionId].notAnsweredCount++;
      }
      
      // Add to total score if marked
      if (submission.markAwarded && submission.markAwarded !== "Pending") {
        const mark = parseFloat(submission.markAwarded) || 0;
        questionStatsMap[questionId].totalScore += mark;
        questionStatsMap[questionId].totalPossibleScore += questionStatsMap[questionId].maxMark;
      }
    });
    
    // Calculate averages and convert to array
    const statsArray = Object.values(questionStatsMap).map(stat => {
      // Calculate average percentage if there were attempts
      if (stat.totalPossibleScore > 0) {
        stat.averagePercentage = (stat.totalScore / stat.totalPossibleScore) * 100;
      }
      
      // Calculate not answered percentage
      if (stat.totalAttempts > 0) {
        stat.notAnsweredPercentage = (stat.notAnsweredCount / stat.totalAttempts) * 100;
      }
      
      return stat;
    });
    
    // Sort by average percentage (ascending) to show most difficult questions first
    return statsArray.sort((a, b) => a.averagePercentage - b.averagePercentage);
  }, [submissions, questions, quizData]);
  
  // Get quiz questions for display
  const quizQuestions = useMemo(() => {
    if (!quizData?.questionIds || !Object.keys(questions).length) {
      return [];
    }
    
    return quizData.questionIds
      .map(id => questions[id])
      .filter(q => q); // Filter out undefined questions
  }, [quizData, questions]);

  // ---------- STYLES ----------
  const containerStyle = {
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  };

  const tileStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    padding: "30px 40px",
    margin: "30px auto",
    maxWidth: "1200px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
  };

  const headerStyle = {
    position: "relative",
    marginBottom: "30px",
    padding: "15px 0",
    borderBottom: "2px solid #c2dcf2",
    textAlign: "center",
  };

  const titleStyle = {
    margin: 0,
    color: "#2980b9",
    fontSize: "32px",
    fontWeight: "600",
    letterSpacing: "0.5px",
  };

  const subtitleStyle = {
    color: "#2980b9",
    fontSize: "18px",
    marginTop: "10px",
    fontWeight: "500",
  };

  const sectionStyle = {
    marginBottom: "30px",
  };

  const sectionTitleStyle = {
    color: "#2980b9",
    fontSize: "24px",
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: "1px solid #c2dcf2",
  };

  const tableContainerStyle = {
    marginTop: "25px",
    overflowX: "auto",
    borderRadius: "12px",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.08)",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    overflow: "hidden",
    borderRadius: "12px",
  };

  const tableHeadStyle = {
    backgroundColor: "#2980b9",
    color: "#fff",
  };

  const tableHeadCellStyle = {
    padding: "16px",
    textAlign: "left",
    fontWeight: "600",
    letterSpacing: "0.5px",
    fontSize: "16px",
    borderBottom: "none",
  };

  const tableBodyCellStyle = {
    padding: "14px 16px",
    borderBottom: "1px solid #c2dcf2",
    fontSize: "15px",
    verticalAlign: "middle",
  };

  const loadingStyle = {
    textAlign: "center",
    padding: "40px 0",
    color: "#2980b9",
    fontSize: "18px",
  };

  const errorStyle = {
    textAlign: "center",
    padding: "40px 0",
    color: "#d9534f",
    fontSize: "18px",
  };

  const backButtonContainerStyle = {
    marginTop: "30px",
    textAlign: "center",
  };

  const backButtonStyle = {
    padding: "12px 24px",
    background: "linear-gradient(45deg, #2980b9, #3498db)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    transition: "all 0.2s",
    transform: isHoveringBack ? "translateY(-2px)" : "none",
    boxShadow: isHoveringBack
      ? "0 6px 12px rgba(41, 128, 185, 0.3)"
      : "0 4px 8px rgba(41, 128, 185, 0.2)",
  };

  const questionCardStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "15px",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    position: "relative",
  };
  
  const questionCardHoverStyle = {
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-2px)",
  };

  const questionTextStyle = {
    fontSize: "16px",
    marginBottom: "10px",
  };

  const questionImageStyle = {
    maxWidth: "100%",
    maxHeight: "300px",
    display: "block",
    margin: "10px 0",
    borderRadius: "4px",
  };

  const noDataStyle = {
    textAlign: "center",
    padding: "20px",
    color: "#666",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px dashed #ddd",
  };
  
  const difficultyCardStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "15px",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
  };
  
  const difficultyHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  };
  
  const difficultyBarContainerStyle = {
    height: "12px",
    backgroundColor: "#e0e0e0",
    borderRadius: "6px",
    overflow: "hidden",
    marginTop: "10px",
    marginBottom: "5px",
  };
  
  const getDifficultyColor = (percentage) => {
    if (percentage < 40) return "#d9534f"; // Red for difficult questions
    if (percentage < 70) return "#f0ad4e"; // Yellow for medium difficulty
    return "#5cb85c"; // Green for easy questions
  };
  
  const getDifficultyLabel = (percentage) => {
    if (percentage < 40) return "Difficult";
    if (percentage < 70) return "Moderate";
    return "Easy";
  };

  return (
    <div style={containerStyle}>
      <div style={tileStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Quiz Results</h1>
          {!loading && !error && quizData && (
            <p style={subtitleStyle}>Quiz Code: {quizCode}</p>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div style={loadingStyle}>Loading quiz results...</div>
        ) : error ? (
          <div style={errorStyle}>{error}</div>
        ) : (
          <>
            {/* Students Section */}
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>Student Results</h2>
              {students.length > 0 ? (
                <div style={tableContainerStyle}>
                  <table style={tableStyle}>
                    <thead style={tableHeadStyle}>
                      <tr>
                        <th style={tableHeadCellStyle}>Username</th>
                        <th style={tableHeadCellStyle}>Email</th>
                        <th style={tableHeadCellStyle}>Score</th>
                        <th style={tableHeadCellStyle}>Max Score</th>
                        <th style={tableHeadCellStyle}>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...students]
                        .sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))
                        .map((student, idx) => (
                        <tr
                          key={student.id}
                          style={{
                            backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f2f7fc",
                          }}
                        >
                          <td style={tableBodyCellStyle}>{student.username}</td>
                          <td style={tableBodyCellStyle}>{student.email}</td>
                          <td style={tableBodyCellStyle}>{student.score}</td>
                          <td style={tableBodyCellStyle}>{student.maxScore}</td>
                          <td style={tableBodyCellStyle}>{student.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={noDataStyle}>
                  No students have completed this quiz yet.
                </div>
              )}
              
            </div>

            {/* Question Difficulty Analysis Section */}
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>Question Difficulty Analysis</h2>
              {questionStats.length > 0 ? (
                <div>
                  {questionStats.map((stat) => (
                    <div key={stat.id} style={difficultyCardStyle}>
                      <div style={difficultyHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                          {stat.questionText}
                        </h3>
                        <div style={{
                          padding: "5px 10px",
                          borderRadius: "4px",
                          backgroundColor: getDifficultyColor(stat.averagePercentage),
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "14px"
                        }}>
                          {getDifficultyLabel(stat.averagePercentage)}
                        </div>
                      </div>
                      
                      {stat.questionImageURL && (
                        <img
                          src={stat.questionImageURL}
                          alt="Question"
                          style={{...questionImageStyle, alignSelf: "center"}}
                        />
                      )}
                      
                      <div style={{ marginTop: "10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                          <span><strong>Average Score:</strong> {stat.averagePercentage.toFixed(1)}%</span>
                          <span><strong>Total Attempts:</strong> {stat.totalAttempts}</span>
                        </div>
                        
                        <div style={difficultyBarContainerStyle}>
                          <div style={{
                            width: `${stat.averagePercentage}%`,
                            height: "100%",
                            backgroundColor: getDifficultyColor(stat.averagePercentage),
                            borderRadius: "6px",
                          }}></div>
                        </div>
                        
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                          <span><strong>Not Answered:</strong> {stat.notAnsweredCount} students ({stat.notAnsweredPercentage.toFixed(1)}%)</span>
                          <span><strong>Average Points:</strong> {(stat.totalScore / stat.totalAttempts).toFixed(1)} / {stat.maxMark}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={noDataStyle}>
                  No question statistics available yet.
                </div>
              )}
            </div>

            {/* Questions Section */}
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>Quiz Questions</h2>
              {quizQuestions.length > 0 ? (
                <div>
                  {quizQuestions.map((question) => {
                    const isExpanded = expandedQuestions[question.id] || false;
                    
                    return (
                      <div 
                        key={question.id} 
                        style={{
                          ...questionCardStyle,
                          ...(isExpanded ? questionCardHoverStyle : {})
                        }}
                        onClick={() => toggleQuestionExpansion(question.id)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.boxShadow = "0 2px 5px rgba(0, 0, 0, 0.1)";
                            e.currentTarget.style.transform = "none";
                          }
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            {question.questionText ? (
                              <p style={questionTextStyle}><strong>Question:</strong> {question.questionText}</p>
                            ) : (
                              <p style={questionTextStyle}><strong>Question:</strong> Image-based question</p>
                            )}
                            <div>
                              <strong>Max Mark:</strong> {question.maxMark || "N/A"}
                            </div>
                          </div>
                          <div style={{ 
                            backgroundColor: isExpanded ? "#e1f0fa" : "#2980b9",
                            color: isExpanded ? "#2980b9" : "#ffffff",
                            fontSize: "14px",
                            fontWeight: "500",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "6px",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            transition: "all 0.2s ease",
                            width: "120px",
                            minWidth: "120px"
                          }}>
                            {isExpanded ? "Hide Answer" : "View Answer"}
                            <span style={{ 
                              fontSize: "12px", 
                              backgroundColor: isExpanded ? "#2980b9" : "#ffffff",
                              color: isExpanded ? "#ffffff" : "#2980b9",
                              width: "18px",
                              height: "18px",
                              borderRadius: "50%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              fontWeight: "bold"
                            }}>
                              {isExpanded ? "−" : "+"}
                            </span>
                          </div>
                        </div>
                        
                        {question.questionImageURL && (
                          <img
                            src={question.questionImageURL}
                            alt="Question"
                            style={questionImageStyle}
                          />
                        )}
                        
                        {isExpanded && (
                          <div style={{
                            marginTop: "15px",
                            padding: "15px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                            transition: "all 0.3s ease",
                          }}>
                            <strong>Mark Scheme Answer:</strong>
                            <div style={{
                              whiteSpace: "pre-wrap",
                              backgroundColor: "#ffffff",
                              padding: "10px",
                              borderRadius: "5px",
                              border: "1px solid #e9ecef",
                              marginTop: "5px",
                              fontFamily: "monospace",
                              lineHeight: "1.5"
                            }}>
                              {question.markSchemeAnswer || "N/A"}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={noDataStyle}>
                  No questions found for this quiz.
                </div>
              )}
            </div>

            {/* Back Button */}
            <div style={backButtonContainerStyle}>
              <button
                onClick={() => navigate("/view-quizzes")}
                style={backButtonStyle}
                onMouseEnter={() => setIsHoveringBack(true)}
                onMouseLeave={() => setIsHoveringBack(false)}
              >
                Back to Quizzes
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuizResults;
